import { html, css, PropertyValues } from 'lit';
import { BaseGenericElement } from './base/BaseElement.js';

export type SkeletonContainerOrientation = 'vertical' | 'horizontal';
export type SkeletonContainerBackground = 'filled' | 'transparent';

export class VeneerSkeletonContainer extends BaseGenericElement {
  declare spacing?: string;

  declare orientation?: SkeletonContainerOrientation;

  declare bordered?: boolean;

  declare padding?: string;

  declare radius?: string;

  declare background?: SkeletonContainerBackground;

  static override properties = {
    /**
     * Sets the space between the elements inside the container.
     * @default '16px'
     */
    spacing: { type: String },
    /**
     * Sets the direction that the container's elements are laid out.
     * @values vertical, horizontal
     * @default 'vertical'
     */
    orientation: { type: String },
    /**
     * If `true`, displays the container with a border.
     * @default true
     */
    bordered: { type: Boolean },
    /**
     * Sets the container's padding.
     * @default '0px'
     */
    padding: { type: String },
    /**
     * Sets the container's optional border radius.
     * @default '8px'
     */
    radius: { type: String },
    /**
     * Sets the container's background style.
     * @values 'filled', 'transparent'
     * @default 'filled'
     * If set to 'transparent', the container will have no background color.
     */
    background: { type: String },
    /**
     * Sets the container's height.
     */
    height: { type: String },
    /**
     * Sets the container's width.
     */
    width: { type: String },
  };

  constructor() {
    super();
    this.spacing = '16px';
    this.orientation = 'vertical';
    this.width = '100%';
    this.height = '';
    this.bordered = true;
    this.padding = '0px';
    this.radius = '8px';
    this.alignment = 'start';
    this.background = 'filled';
  }

  protected override updated(_changedProperties: PropertyValues): void {
    const components =
      'veneer-skeleton-header, veneer-skeleton-shape, veneer-skeleton-text';

    // Set the theme alignment for all children components
    if (_changedProperties.has('alignment')) {
      this.querySelectorAll(components).forEach(el => {
        if (this.alignment) {
          el.setAttribute('theme-alignment', this.alignment);
        }
      });
    }
  }

  override render() {
    return html`
      <div
        class="vn-skel-loading__container"
        style="
        --vn-skeleton-container-spacing: ${this.spacing};
        --vn-skeleton-container-width: ${this.width};
        --vn-skeleton-container-height: ${this.height};
        --vn-skeleton-container-padding: ${this.padding};
        --vn-skeleton-container-border-radius: ${this.radius};
        --vn-skeleton-container-flex-direction: ${this.orientation ===
        'horizontal'
          ? 'row'
          : 'column'};
      "
      >
        <slot></slot>
      </div>
    `;
  }

  static override styles = [
    css`
      :host {
        display: block;
        box-sizing: border-box;
      }

      :host > div {
        width: var(--vn-skeleton-container-width);
        height: var(--vn-skeleton-container-height);
        display: flex;
        box-sizing: border-box;
        background-color: var(--vn-skeleton-background-color__container);
        padding: var(--vn-skeleton-container-padding);
        border-radius: var(--vn-skeleton-container-border-radius);
        flex-direction: var(--vn-skeleton-container-flex-direction);
        border: 1px solid var(--vn-skeleton-stroke-color__container);
        gap: var(--vn-skeleton-container-spacing);
      }
      :host([background='transparent']) > div {
        background-color: transparent;
      }
      :host([bordered='false']) > div {
        border: none;
      }
      :host([orientation='vertical']) ::slotted(*:not(:last-child)) {
        margin-bottom: var(--vn-skeleton-container-spacing);
      }

      :host([orientation='horizontal']) ::slotted(*:not(:last-child)) {
        margin-right: var(--vn-skeleton-container-spacing);
      }
      :host([orientation='horizontal']) > div {
        align-items: center;
        -webkit-align-items: center;
      }
    `,
  ];
}

if (!customElements.get('veneer-skeleton-container')) {
  customElements.define('veneer-skeleton-container', VeneerSkeletonContainer);
}

declare global {
  interface HTMLElementTagNameMap {
    'veneer-skeleton-container': VeneerSkeletonContainer;
  }
}
